import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProgressBar from "../components/Registration/ProgressBar";
import StepRenderer from "../components/Registration/StepRenderer";
import DocumentsStepRenderer from "../components/Registration/DocumentsStepRenderer";
import RegistrationSummary from "../components/Registration/RegistrationSummary";
import { useRegistration } from "hooks/crm/useRegistration";
import { useApplicationSchema } from "hooks/useApplicationSchema";
import { requireAuth } from "utilities/auth";
import PageLoader from "../components/Loaders/PageLoader";
import BugNub from "components/BugNub";

function Registration() {
  const { schema, isLoading: schemaLoading } = useApplicationSchema();

  const {
    currentStep,
    registrationData,
    storedData,
    handleDataChange,
    handleNext,
    handlePrevious,
    handleSubmit,
    handleDocumentUpload,
    handleDocumentDelete,
    canProceed,
    loadState,
    STEPS,
  } = useRegistration(schema);

  const renderStep = () => {
    if (!schema) return null;

    const stepIndex = currentStep - 1;

    // Field steps (driven by schema)
    if (stepIndex < schema.steps.length) {
      return (
        <StepRenderer
          stepSchema={schema.steps[stepIndex]}
          data={registrationData}
          storedData={storedData}
          onChange={handleDataChange}
        />
      );
    }

    // Documents step
    if (stepIndex === schema.steps.length) {
      return (
        <DocumentsStepRenderer
          documentSchema={schema.documents}
          data={registrationData}
          handleDocumentUpload={handleDocumentUpload}
          handleDocumentDelete={handleDocumentDelete}
        />
      );
    }

    // Review & Submit step
    return (
      <RegistrationSummary
        data={registrationData}
        schema={schema}
        onSubmit={handleSubmit}
      />
    );
  };

  // SOUND-726. This was `if (schemaLoading || !registrationData) return null;`
  // — a single blank return covering four different situations, three of which
  // never resolve. A merchant whose application failed to load, or who had no
  // application at all, sat on an empty page indefinitely with nothing to click
  // and nothing to read. It is the same silent blank RequireRole produced, ten
  // lines into a different file, on the same route.
  if (loadState === "error") {
    return (
      <div className="dark min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md w-full surface p-6 text-center">
          <h1 className="text-xl font-semibold mb-2">
            We could not load your registration
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            Something went wrong on our side. Your details are safe — please try
            again, or contact us if it keeps happening.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-lg bg-brand text-background text-sm font-semibold hover:bg-brand-400 transition"
            >
              Try again
            </button>
            <a
              href="mailto:support@sound-commerce.com"
              className="px-5 py-2.5 rounded-lg border border-border text-sm font-semibold hover:bg-muted transition"
            >
              Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Resolving, or resolved to somewhere else and the redirect is in flight.
  // Show the loader rather than a frame of a wizard this merchant is about to
  // be moved off — a form that appears and vanishes reads as a glitch, and on a
  // slow connection it is long enough to start typing into.
  if (schemaLoading || loadState !== "ready" || !registrationData) {
    return <PageLoader />;
  }

  return (
    <div className="dark min-h-screen bg-background text-foreground font-jakarta font-light">
      <div className="container mx-auto px-6 md:px-[75px] py-[100px]">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl leading-tight mb-4 font-light">
              Business Registration<span className="text-brand">.</span>
            </h1>
            <p className="text-md md:text-lg text-muted-foreground font-light max-w-3xl mx-auto">
              Register your business and let our experts recommend the best
              payment processors for you
            </p>
          </div>

          {/* Progress Bar */}
          <ProgressBar currentStep={currentStep} totalSteps={STEPS.length} />

          {/* Step Content */}
          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <div className="min-h-[500px]">{renderStep()}</div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`
                inline-flex items-center px-6 py-3 border text-base font-semibold rounded-lg transition
                ${
                  currentStep === 1
                    ? "text-muted-foreground bg-card border-border cursor-not-allowed opacity-50"
                    : "text-foreground bg-card border-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand"
                }
              `}
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </button>

            <span className="text-sm text-muted-foreground font-light">
              {STEPS[currentStep - 1]}
            </span>

            {currentStep < STEPS.length ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`
                  inline-flex items-center px-6 py-3 text-base font-semibold rounded-lg transition
                  ${
                    canProceed()
                      ? "text-background bg-brand hover:bg-brand-400 focus:outline-none focus:ring-2 focus:ring-brand"
                      : "text-muted-foreground bg-card border border-border cursor-not-allowed opacity-50"
                  }
                `}
              >
                Next
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            ) : (
              <div className="w-24" />
            )}
          </div>

          {/* SOUND-794: the wizard sits outside AccountLayout, so it has no
              top bar to carry Feedback. Inline here, below the controls, it
              is part of the flow and cannot cover Previous/Next. */}
          <div className="mt-10 flex justify-center">
            <BugNub />
          </div>
        </div>
      </div>
    </div>
  );
}

export default requireAuth(Registration);
